/**
 * ProjectActivityLog Component
 *
 * Displays project-level activity (created, updated, activated, deactivated)
 * with expandable rows for field-level change details.
 */

import { useState } from 'react';
import {
  Folder,
  ChevronDown,
  ChevronRight,
  Download,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { EmptyState } from '@/components/EmptyState';
import { toast } from '@/lib/toast';
import { cn, formatTimestamp, formatRelativeTime } from '@/lib/utils';
import { useProjectLogs } from '@/hooks/useActivityLogs';
import type { ProjectLogEntry } from '@/types/domain';

interface ProjectActivityLogProps {
  projectId: string;
}

const actionConfig: Record<string, { label: string; className: string }> = {
  created: { label: 'Created', className: 'bg-success/10 text-success' },
  updated: { label: 'Updated', className: 'bg-info/10 text-info' },
  activated: { label: 'Activated', className: 'bg-success/10 text-success' },
  deactivated: { label: 'Deactivated', className: 'bg-warning/10 text-warning' },
};

function ProjectLogRow({ entry }: { entry: ProjectLogEntry }) {
  const [expanded, setExpanded] = useState(false);
  const config = actionConfig[entry.action] ?? actionConfig.created;
  const hasChanges = entry.action === 'updated' && entry.changes && Object.keys(entry.changes).length > 0;

  return (
    <>
      <TableRow
        className={cn(hasChanges && 'cursor-pointer hover:bg-accent/5')}
        onClick={hasChanges ? () => setExpanded(!expanded) : undefined}
        role={hasChanges ? 'button' : undefined}
        tabIndex={hasChanges ? 0 : undefined}
        aria-expanded={hasChanges ? expanded : undefined}
        onKeyDown={hasChanges ? (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded(!expanded);
          }
        } : undefined}
      >
        <TableCell>
          <div className="flex items-center gap-1">
            {hasChanges && (
              expanded
                ? <ChevronDown className="h-3 w-3 text-muted-foreground" />
                : <ChevronRight className="h-3 w-3 text-muted-foreground" />
            )}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="space-y-0.5">
                    <span className="text-sm">{formatTimestamp(entry.timestamp)}</span>
                    <span className="block text-xs text-muted-foreground">{formatRelativeTime(entry.timestamp)}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  {new Date(entry.timestamp).toLocaleString()}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </TableCell>
        <TableCell>
          <Badge variant="outline" className={config.className}>
            {config.label}
          </Badge>
        </TableCell>
        <TableCell className="text-sm">{entry.actor_name}</TableCell>
        <TableCell className="text-sm text-muted-foreground">
          {hasChanges
            ? `${Object.keys(entry.changes!).length} field${Object.keys(entry.changes!).length !== 1 ? 's' : ''} changed`
            : '—'}
        </TableCell>
      </TableRow>
      {expanded && hasChanges && (
        <tr>
          <td colSpan={4} className="p-0">
            <div className="px-6 py-3 bg-muted/30 border-t space-y-2">
              {Object.entries(entry.changes!).map(([field, change]) => (
                <div key={field} className="flex items-center gap-2 text-sm">
                  <Badge variant="outline" className="text-xs font-mono shrink-0">
                    {field}
                  </Badge>
                  <span className="text-muted-foreground truncate" title={change.from || '(empty)'}>
                    {change.from || '(empty)'}
                  </span>
                  <span className="shrink-0">→</span>
                  <span className="font-medium text-foreground truncate" title={change.to || '(empty)'}>
                    {change.to || '(empty)'}
                  </span>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export function ProjectActivityLog({ projectId }: ProjectActivityLogProps) {
  const { data, isLoading } = useProjectLogs(projectId, { refetchInterval: 30000 });
  const [showAll, setShowAll] = useState(false);

  const handleExport = () => {
    const logs = data?.logs ?? [];
    const csv = [
      'Timestamp,Action,Actor,Changes',
      ...logs.map(log => {
        const changes = log.changes
          ? Object.entries(log.changes).map(([f, c]) => `${f}: ${c.from} → ${c.to}`).join('; ')
          : '';
        return `"${new Date(log.timestamp).toISOString()}","${log.action}","${log.actor_name}","${changes}"`;
      }),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `project-activity-${projectId}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Project activity exported to CSV');
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Project Activity</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  const logs = data?.logs ?? [];

  if (logs.length === 0) {
    return (
      <EmptyState
        icon={Folder}
        title="No Project Activity"
        description="Project activity will appear here when changes are made."
      />
    );
  }

  const displayLogs = showAll ? logs : logs.slice(0, 5);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Folder className="h-5 w-5 text-primary" />
              Project Activity
            </CardTitle>
            <CardDescription>
              {logs.length} project event{logs.length !== 1 ? 's' : ''}
            </CardDescription>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleExport}
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[160px]">Time</TableHead>
              <TableHead className="w-[110px]">Action</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayLogs.map((entry, idx) => (
              <ProjectLogRow key={`${entry.timestamp}-${idx}`} entry={entry} />
            ))}
          </TableBody>
        </Table>
        {logs.length > 5 && (
          <div className="mt-3 text-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAll(!showAll)}
              className="text-muted-foreground"
            >
              {showAll ? 'Show less' : `Show all ${logs.length} entries`}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
